const isObject = (v) => Boolean(v) && typeof v === 'object' && !Array.isArray(v);
function parseIndex(v) {
    if (!isObject(v) || v.v !== 1 || typeof v.total !== 'number' || !Number.isInteger(v.total) || v.total < 1 || v.total > 100000 || v.size !== 400 || typeof v.pages !== 'number' || v.pages !== Math.ceil(v.total / 400) || !Array.isArray(v.rows) || v.rows.length !== v.total)
        throw Error('Invalid review index');
    const rows = [];
    for (const row of v.rows) {
        if (!Array.isArray(row) || row.length !== 3 || !Number.isInteger(row[0]) || row[0] < 1 || row[0] > 5 || !Number.isSafeInteger(row[1]) || row[1] < 0 || typeof row[2] !== 'boolean')
            throw Error('Invalid review metadata');
        rows.push([row[0], row[1], row[2]]);
    }
    if (!Array.isArray(v.chunks) || v.chunks.length !== v.pages || v.chunks.some((name, i) => typeof name !== 'string' || !new RegExp(`^cp-review-data-${i}-[a-f0-9]{12}\\.json$`).test(name)))
        throw Error('Invalid chunk manifest');
    return { v: 1, total: v.total, size: 400, pages: v.pages, rows, chunks: v.chunks };
}
function parseReviews(v, page) {
    if (!isObject(v) || v.v !== 1 || v.p !== page || !Array.isArray(v.reviews) || v.reviews.length > 400)
        throw Error('Invalid review chunk');
    const output = [];
    for (const row of v.reviews) {
        if (!isObject(row) || typeof row.a !== 'string' || row.a.length > 200 || typeof row.r !== 'number' || !Number.isInteger(row.r) || row.r < 1 || row.r > 5 || typeof row.d !== 'number' || !Number.isSafeInteger(row.d) || row.d < 0 || typeof row.b !== 'string' || row.b.length > 20000 || typeof row.p !== 'string' || row.p !== '' && !/^cp-review-(photo-[a-f0-9]{20}|poster-[a-f0-9]{32})\.jpg$/.test(row.p) || typeof row.s !== 'boolean' || typeof row.o !== 'string' || row.o.length > 300)
            throw Error('Invalid review');
        if (typeof row.v !== 'string' || row.v !== '' && !/^cp-review-video-[a-z0-9-]{1,40}\.mp4$/.test(row.v))
            throw Error('Invalid review video');
        if (typeof row.i !== 'string' || !/^[\w-]{1,80}$/.test(row.i) || typeof row.q !== 'boolean')
            throw Error('Invalid review identity');
        if (row.l !== 'he' && row.l !== 'en')
            throw Error('Invalid review language');
        output.push({ a: row.a, r: row.r, d: row.d, b: row.b, l: row.l, p: row.p, v: row.v, s: row.s, o: row.o, i: row.i, q: row.q });
    }
    return output;
}
async function readJson(url, max) {
    const response = await fetch(url, { credentials: 'omit', signal: AbortSignal.timeout(20000) });
    if (!response.ok || Number(response.headers.get('content-length') ?? 0) > max)
        throw Error('Cannot load reviews');
    const raw = await response.text();
    if (raw.length > max)
        throw Error('Review response too large');
    return JSON.parse(raw);
}
function element(tag, className = '', text = '') { const el = document.createElement(tag); el.className = className; el.textContent = text; return el; }
export function mountReviews(root = document) {
    root.querySelectorAll('[data-reviews]').forEach(container => {
        if (container.dataset.mounted)
            return;
        container.dataset.mounted = 'true';
        const status = container.querySelector('[data-review-status]');
        const grid = container.querySelector('[data-review-grid]');
        const controls = container.querySelector('[data-review-controls]');
        const summary = container.querySelector('[data-review-summary]');
        const sort = container.querySelector('[data-review-sort]');
        const photos = container.querySelector('[data-review-photos]');
        const previous = container.querySelector('[data-review-prev]');
        const next = container.querySelector('[data-review-next]');
        const pageField = container.querySelector('[data-review-page]');
        const pagesLabel = container.querySelector('[data-review-pages]');
        const load = container.querySelector('[data-review-load]');
        if (!status || !grid || !controls || !summary || !sort || !photos || !previous || !next || !pageField || !pagesLabel || !load)
            return;
        const raw = container.dataset.reviewIndex;
        if (!raw)
            return;
        let base;
        try {
            base = new URL(raw, location.origin);
        }
        catch {
            return;
        }
        if (!(base.origin === location.origin && ['https:', 'http:'].includes(base.protocol) || base.protocol === 'https:' && base.hostname === 'cdn.shopify.com'))
            return;
        let index;
        let order = [];
        let page = 0;
        let rating = 0;
        let serial = 0;
        let busy = false;
        const perPage = 12;
        const cache = new Map();
        const asset = (name) => new URL(name, base);
        const chunk = async (p) => {
            const existing = cache.get(p);
            if (existing)
                return existing;
            const name = index?.chunks[p];
            if (!name)
                throw Error('Missing chunk manifest');
            const result = parseReviews(await readJson(asset(name), 12_000_000), p);
            cache.set(p, result);
            if (cache.size > 24) {
                const key = cache.keys().next().value;
                if (key !== undefined)
                    cache.delete(key);
            }
            return result;
        };
        const render = async () => {
            if (!index)
                return;
            const token = ++serial;
            const totalPages = Math.max(1, Math.ceil(order.length / perPage));
            page = Math.max(0, Math.min(page, totalPages - 1));
            pageField.value = String(page + 1);
            pageField.max = String(totalPages);
            pagesLabel.textContent = `מתוך ${totalPages.toLocaleString('he-IL')}`;
            previous.disabled = page === 0;
            next.disabled = page + 1 >= totalPages;
            status.textContent = 'טוענים חוות דעת…';
            grid.setAttribute('aria-busy', 'true');
            grid.replaceChildren();
            try {
                const positions = order.slice(page * perPage, (page + 1) * perPage);
                const chunks = [...new Set(positions.map(i => Math.floor(i / 400)))];
                const loaded = new Map(await Promise.all(chunks.map(async (p) => [p, await chunk(p)])));
                if (token !== serial)
                    return;
                const cards = positions.map(i => {
                    const review = loaded.get(Math.floor(i / 400))?.[i % 400];
                    if (!review)
                        throw Error('Missing review');
                    const card = element('article', 'review-card');
                    if (review.v) {
                        const video = element('video');
                        video.src = asset(review.v).href;
                        video.controls = true;
                        video.muted = true;
                        video.playsInline = true;
                        video.preload = 'none';
                        video.width = 540;
                        video.height = 720;
                        video.setAttribute('aria-label', 'סרטון שצורף לחוות הדעת אצל הספק, ללא קול');
                        if (review.p)
                            video.poster = asset(review.p).href;
                        card.append(video);
                    }
                    else if (review.p) {
                        const a = element('a', 'review-photo-link');
                        a.href = asset(review.p).href;
                        a.target = '_blank';
                        a.rel = 'noopener';
                        a.setAttribute('aria-label', 'פתיחת תמונת הלקוח');
                        const img = element('img');
                        img.src = a.href;
                        img.alt = 'תמונה או תצוגת מדיה שצורפה לחוות הדעת אצל הספק';
                        img.loading = 'lazy';
                        img.width = 400;
                        img.height = 400;
                        a.append(img);
                        card.append(a);
                    }
                    const content = element('div', 'review-card-copy');
                    const author = element('h3', 'review-author', review.a);
                    author.dir = 'auto';
                    content.append(author);
                    const stars = element('p', 'review-stars', '★'.repeat(review.r) + '☆'.repeat(5 - review.r));
                    stars.setAttribute('aria-label', `${review.r} מתוך 5`);
                    content.append(stars);
                    const date = element('time', 'review-date', new Date(review.d * 1000).toLocaleDateString('he-IL'));
                    date.dateTime = new Date(review.d * 1000).toISOString().slice(0, 10);
                    content.append(date);
                    const body = element('p', 'review-body', review.b);
                    body.dir = 'auto';
                    body.lang = review.l;
                    content.append(body);
                    content.append(element('span', 'review-source', (review.s ? 'מסומן אצל הספק כרכישה מאומתת' : 'חוות דעת מהספק') + (review.l === 'he' ? ' · תורגם מאנגלית' : '')));
                    card.append(content);
                    return card;
                });
                grid.replaceChildren(...cards);
                status.textContent = order.length ? `${order.length.toLocaleString('he-IL')} חוות דעת בבחירה זו` : 'אין חוות דעת במסנן הזה';
            }
            catch {
                if (token === serial)
                    status.textContent = 'לא ניתן לטעון את חוות הדעת כרגע. אפשר לנסות שוב.';
            }
            finally {
                if (token === serial)
                    grid.removeAttribute('aria-busy');
            }
        };
        const filter = () => { if (!index)
            return; const data = index; order = data.rows.map((_, i) => i).filter(i => (!rating || data.rows[i]?.[0] === rating) && (!photos.checked || data.rows[i]?.[2])); if (sort.value === 'newest')
            order.sort((a, b) => (data.rows[b]?.[1] ?? 0) - (data.rows[a]?.[1] ?? 0)); if (sort.value === 'high')
            order.sort((a, b) => (data.rows[b]?.[0] ?? 0) - (data.rows[a]?.[0] ?? 0)); if (sort.value === 'low')
            order.sort((a, b) => (data.rows[a]?.[0] ?? 0) - (data.rows[b]?.[0] ?? 0)); page = 0; void render(); };
        const start = async () => {
            if (busy || index)
                return;
            busy = true;
            load.disabled = true;
            status.textContent = 'טוענים את רשימת הביקורות…';
            try {
                index = parseIndex(await readJson(base, 4_000_000));
                const mean = index.rows.reduce((sum, row) => sum + row[0], 0) / index.total;
                const score = element('strong', 'review-score', mean.toFixed(1));
                const label = element('p', '', `${index.total.toLocaleString('he-IL')} חוות דעת מהספק על הדגם`);
                const bars = element('div', 'review-bars');
                for (let r = 5; r >= 1; r--) {
                    const count = index.rows.filter(row => row[0] === r).length;
                    const button = element('button', 'review-bar');
                    button.type = 'button';
                    button.setAttribute('aria-label', `סינון לפי ${r} כוכבים, ${count} חוות דעת`);
                    button.setAttribute('aria-pressed', 'false');
                    button.append(element('span', '', `${r} ★`));
                    const track = element('span', 'review-bar-track');
                    const fill = element('span');
                    fill.style.width = `${100 * count / index.total}%`;
                    track.append(fill);
                    button.append(track, element('span', '', count.toLocaleString('he-IL')));
                    button.addEventListener('click', () => { rating = rating === r ? 0 : r; bars.querySelectorAll('button').forEach(b => b.setAttribute('aria-pressed', String(b === button && rating === r))); filter(); });
                    bars.append(button);
                }
                summary.replaceChildren(score, label, bars);
                summary.hidden = false;
                controls.hidden = false;
                load.hidden = true;
                filter();
            }
            catch {
                status.textContent = 'לא ניתן לטעון את הביקורות כרגע.';
                load.disabled = false;
            }
            finally {
                busy = false;
            }
        };
        load.addEventListener('click', () => void start());
        sort.addEventListener('change', filter);
        photos.addEventListener('change', filter);
        previous.addEventListener('click', () => { page--; void render(); });
        next.addEventListener('click', () => { page++; void render(); });
        pageField.addEventListener('change', () => { if (!/^\d{1,5}$/.test(pageField.value))
            return; page = Number(pageField.value) - 1; void render(); });
        if ('IntersectionObserver' in window) {
            const observer = new IntersectionObserver(entries => { if (entries.some(e => e.isIntersecting)) {
                observer.disconnect();
                void start();
            } }, { rootMargin: '250px' });
            observer.observe(container);
        }
    });
}
if (typeof document !== 'undefined') {
    mountReviews();
    document.addEventListener('shopify:section:load', () => mountReviews());
}
